import { Grid } from '@material-ui/core';
import useToast from '@yaydoo/react-components/lib/hooks/useToast';
import { observer } from 'mobx-react';
import React, { FC } from 'react';
import { SubmitHandler, useFormContext } from 'react-hook-form';
import { FieldValues } from 'react-hook-form/dist/types';
import { useTranslation } from 'react-i18next';

import PayButton from '@components/PayButton';
import { Partner } from '@constants/paymentLink';
import { IGenericSpeiPaymentData, PaymentFormProps } from '@containers/checkout/payment-form/types';
import useSmartFields from '@hooks/useSmartFields';

import useStyles from './styles';
import FormPartnerLogo from '@components/FormPartnerLogo';

export interface IGenericSpeiConfirmation extends PaymentFormProps {
  isAmountAllowedToCardPayment: boolean;
  isLoadingSpeiReference: boolean;
  isAllowedToPayWithSpei: boolean;
  paymentLinkId: string;
  partner: Partner;
  getSpeiReference: (value: IGenericSpeiPaymentData) => void;
}

const GenericSpeiConfirmation: FC<IGenericSpeiConfirmation> = ({
  isAmountAllowedToCardPayment,
  isLoadingSpeiReference,
  isAllowedToPayWithSpei,
  paymentLinkId,
  partner,
  getSpeiReference,
  onValidationError = () => null,
}) => {
  const css = useStyles();
  const { t } = useTranslation();
  const { handleSubmit } = useFormContext();
  const { deviceId } = useSmartFields({ apiKey: '' });
  const { openToast } = useToast();

  const pay = async (values: IGenericSpeiPaymentData) => {
    const data = { ...values, paymentLinkId, deviceId, partner };
    try {
      await getSpeiReference(data);
    } catch (error) {
      const { msg = '' } = error as { msg: string };
      openToast({
        type: 'error',
        title: t('checkoutScreen.payment.errors.spei.title'),
        message: msg || t('checkoutScreen.payment.errors.spei.unknown'),
      });
    }
  };

  return (
    <div>
      <Grid container spacing={1}>
        {!isAmountAllowedToCardPayment && (
          <span className={css.limitExceeded}>
            {t('checkoutScreen.payment.methods.spei.limitExceeded')}
          </span>
        )}

        <Grid item xs={12} data-testid="spei-generic-confirmation-info">
          {t('checkoutScreen.payment.methods.spei.info')}
        </Grid>
      </Grid>

      <PayButton
        onClick={handleSubmit(pay as SubmitHandler<FieldValues>, onValidationError)}
        disabled={isLoadingSpeiReference || !isAllowedToPayWithSpei}
        loading={isLoadingSpeiReference}
        label={t('checkoutScreen.payment.methods.submit')}
      />
      <FormPartnerLogo partner={partner} />
    </div>
  );
};

export default observer(GenericSpeiConfirmation);
